import get from 'lodash/get';
import moment from 'moment';
import { PostQueryOperations, QueryFilter } from '../../../@types';
import { parseDataValue } from '../../../utils/dataUtils';

/* eslint-disable @typescript-eslint/no-explicit-any */
// We need this definitions to accept any
const filterMap: Record<string, (a: any, b: any) => boolean> = {
  isNull: (value: any, filter: any) => {
    const comparison = typeof value === 'string' ? 'null' : null;
    return filter === true ? value === comparison : value !== comparison;
  },
  // Equality
  equalTo: (value: any, filter: any) => value === filter,
  notEqualTo: (value: any, filter: any) => value !== filter,
  // In / Not in
  in: (value: any, filter: Array<any>) => !!filter?.includes(value),
  notIn: (value: any, filter: Array<any>) => !filter?.includes(value),
  // Comparisons
  greaterThanOrEqualTo: (value: any, filter: any) => value >= filter,
  greaterThan: (value: any, filter: any) => value > filter,
  lessThanOrEqualTo: (value: any, filter: any) => value <= filter,
  lessThan: (value: any, filter: any) => value < filter,
};
/* eslint-enable @typescript-eslint/no-explicit-any */

/**
 * Array sort comparator for numbers
 */
const getSortNumber =
  (ordering: Required<PostQueryOperations>['ordering']) =>
  (a: Record<string, unknown>, b: Record<string, unknown>) => {
    const aValue = parseDataValue(a, ordering.key, 'decimal') as number;
    const bValue = parseDataValue(b, ordering.key, 'decimal') as number;
    return ordering.direction === 'desc' ? bValue - aValue : aValue - bValue;
  };

/**
 * Array sort comparator for strings
 */
const getSortString =
  (ordering: Required<PostQueryOperations>['ordering']) =>
  (a: Record<string, unknown>, b: Record<string, unknown>) => {
    const aValue = (
      parseDataValue(a, ordering.key, 'string') as string
    )?.toUpperCase();
    const bValue = (
      parseDataValue(b, ordering.key, 'string') as string
    )?.toUpperCase();

    if (aValue < bValue) {
      return ordering.direction === 'desc' ? 1 : -1;
    }
    if (aValue > bValue) {
      return ordering.direction === 'desc' ? -1 : 1;
    }
    return 0;
  };

/**
 * Array sort comparator for dates
 */
const getSortDate =
  (ordering: Required<PostQueryOperations>['ordering']) =>
  (a: Record<string, unknown>, b: Record<string, unknown>) => {
    const aValue = moment(parseDataValue(a, ordering.key, 'string') as string);
    const bValue = moment(parseDataValue(b, ordering.key, 'string') as string);
    return ordering.direction === 'desc'
      ? bValue.diff(aValue)
      : aValue.diff(bValue);
  };

/**
 * Array sort comparator for intervals
 */
const getSortInterval =
  (ordering: Required<PostQueryOperations>['ordering']) =>
  (a: Record<string, unknown>, b: Record<string, unknown>) => {
    const aValue = parseInt(
      parseDataValue(a, ordering.key, 'interval') as string,
      10,
    );
    const bValue = parseInt(
      parseDataValue(b, ordering.key, 'interval') as string,
      10,
    );
    return ordering.direction === 'desc' ? bValue - aValue : aValue - bValue;
  };

//
// Filter Data
//

export type IsDataValidFn = (
  data: Record<string, unknown>,
  filters?: Array<QueryFilter>,
) => boolean;

/**
 * Returns if data is valid accordingly to QueryFilter definitions
 */
export const isDataValid: IsDataValidFn = (data, filters) => {
  if (!filters) return true;

  // Filter data if needed
  return filters.every((f) => {
    const parsedValue = parseDataValue(data, f.key, f.dataType || 'string');
    return Object.entries(f.statements).every(([op, ref]) => {
      const fn = filterMap[op];
      return fn ? fn(parsedValue, ref) : true;
    });
  });
};

export type FilterDataFn = (
  data: Record<string, unknown> | Record<string, unknown>[],
  rootKey?: string,
  postQuery?: PostQueryOperations,
) => Array<Record<string, unknown>>;

/**
 * Filters data applying postQueryFilters
 */
export const filterData: FilterDataFn = (data, rootKey, postQuery) => {
  const fetchedUnknown = rootKey ? get(data, rootKey, []) : data || [];
  const fetched = Array.isArray(fetchedUnknown)
    ? [...fetchedUnknown]
    : [fetchedUnknown];
  if (!postQuery) return fetched;

  // Filter data if needed
  const { filters } = postQuery;
  const filtered = !filters
    ? fetched
    : fetched.filter((d) => isDataValid(d, filters));

  // order data if needed
  // @Note: for now we assume the ordering values are numeric
  const { ordering } = postQuery;
  if (ordering) {
    let sortFn;

    if (ordering.dataType === 'number') {
      sortFn = getSortNumber(ordering);
    }

    if (ordering.dataType === 'string') {
      sortFn = getSortString(ordering);
    }

    if (ordering.dataType === 'date') {
      sortFn = getSortDate(ordering);
    }

    if (ordering.dataType === 'interval') {
      sortFn = getSortInterval(ordering);
    }

    filtered.sort(sortFn);
  }

  // limit, offset data values if needed
  const { limit, offset } = postQuery;
  const start = offset || 0;
  const limited =
    limit && limit > 0
      ? filtered.slice(start, start + limit)
      : filtered.slice(start);

  return limited;
};
